const createFaqSchema = (config, list) => {
  if (!list || !list.length) return null;
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: list.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
  return faqSchema;
};
const createSchema = (config, { meta_description, meta_image }) => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: config.public.PROJECT_NAME,
    url: config.public.ORIGIN_URL,
    description: meta_description,
    creator: {
      "@type": "Organization",
      name: config.public.PROJECT_NAME,
      url: config.public.ORIGIN_URL,
    },
    image: {
      "@type": "ImageObject",
      url: meta_image?.url || config.public.ORIGIN_URL + "/OpenGraph.jpg",
      width: 1200,
      height: 630,
    },
  };
};
const createArticleSchema = (
  config,
  article,
  { url, meta_image, meta_description }
) => {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.data?.title,
    description: meta_description,
    image:
      article.data?.poster?.url ||
      meta_image?.url ||
      config.public.ORIGIN_URL + "/OpenGraph.jpg",
    author: {
      "@type": "Person",
      name: config.public.PROJECT_NAME,
    },
    datePublished: article.first_publication_date,
    dateModified: article.last_publication_date,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": config.public.ORIGIN_URL + url,
    },
  };
};

export default defineNuxtPlugin((nuxtApp) => {
  const route = useRoute();

  nuxtApp.provide("head", (payload, additional = {}) => {
    const config = useRuntimeConfig();
    const currentUrl = `${config.public.ORIGIN_URL}${route.fullPath}`;

    const {
      meta_title,
      meta_description,
      meta_keywords,
      meta_image,
      title,
      noindex,
      nofollow,
      canonical,
      type, // og:type
      url, // og:url
      alternate_languages,
      lastmodified,
      faq,
      article,
    } = payload;

    const link = [];

    const meta = [
      {
        hid: "og:title",
        name: "og:title",
        content: meta_title || "",
      },
      {
        hid: "description",
        name: "description",
        content: meta_description || "",
      },
      {
        hid: "keywords",
        name: "keywords",
        content: meta_keywords || "",
      },
      {
        hid: "og:description",
        name: "og:description",
        content: meta_description || "",
      },
      {
        hid: "og:type",
        name: "og:type",
        content: type || "website",
      },
      {
        hid: "og:image",
        property: "og:image",
        content: meta_image?.url || config.public.ORIGIN_URL + "/OpenGraph.jpg",
      },
      {
        hid: "twitter:card",
        property: "twitter:card",
        content: "summary_large_image",
      },
      {
        hid: "twitter:title",
        property: "twitter:title",
        content: meta_title || "",
      },
      {
        hid: "twitter:description",
        property: "twitter:description",
        content: meta_description || "",
      },
      {
        hid: "twitter:image",
        property: "twitter:image",
        content: meta_image?.url || config.public.ORIGIN_URL + "/OpenGraph.jpg",
      },
      {
        hid: "og:url",
        property: "og:url",
        content: url || currentUrl,
      },
      {
        hid: "lastmodified",
        name: "lastmodified",
        content: lastmodified || new Date().toISOString(),
      },
      {
        hid: "og:logo",
        name: "og:logo",
        content: config.public.ORIGIN_URL + "/favicon/favicon.svg",
      },
    ];

    // robots
    if (noindex || nofollow) {
      meta.push({
        hid: "robots",
        name: "robots",
        content: (() => {
          if (noindex && nofollow) return "noindex, nofollow";
          if (noindex) return "noindex";
          if (nofollow) return "nofollow";
        })(),
      });
    }

    // canonical
    link.push({
      rel: "canonical",
      hid: "canonical",
      href: canonical || currentUrl,
    });

    const script = [];

    script.push({
      hid: "schema",
      type: "application/ld+json",
      innerHTML: JSON.stringify(createSchema(config, payload)),
    });

    if (faq) {
      const faqSchema = createFaqSchema(config, faq);
      if (faqSchema) {
        script.push({
          hid: "schema",
          type: "application/ld+json",
          innerHTML: JSON.stringify(faqSchema),
        });
      }
    }

    if (article) {
      const articleSchema = createArticleSchema(config, article, payload);
      script.push({
        hid: "schema",
        type: "application/ld+json",
        innerHTML: JSON.stringify(articleSchema),
      });
    }

    return {
      meta,
      link,
      title: meta_title || title,
      htmlAttrs: {
        lang: "en",
      },
      ...additional,
      script,
    };
  });
});
